import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const StyledCanvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 10;
`;

const MouseTrail = ({ color = '#64ffda' }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof window === 'undefined') return;

    const ctx = canvas.getContext('2d');
    if (!ctx || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Solo habilitar si el dispositivo cuenta con un cursor de precisión (ratón o trackpad)
    if (window.matchMedia('(pointer: coarse)').matches && !window.matchMedia('(pointer: fine)').matches) {
      return;
    }

    let width = 0;
    let height = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener('resize', resize);

    let lastPos = null;

    // Dibuja los puntos alineados a la matriz de 7px dentro del radio
    const drawDotGrid = (x, y) => {
      const step = 7;
      const radius = 28;
      const startX = step * Math.round((x - radius) / step);
      const startY = step * Math.round((y - radius) / step);

      for (let nx = startX; nx <= x + radius; nx += step) {
        for (let ny = startY; ny <= y + radius; ny += step) {
          const dist = Math.hypot(nx - x, ny - y);
          if (dist > radius) continue;

          const factor = 1 - dist / radius;
          if (Math.random() < 0.45 * factor) {
            ctx.globalAlpha = 0.35 * factor + 0.05;
            ctx.fillStyle = color;
            ctx.fillRect(nx, ny, 1.5, 1.5);
          }
        }
      }
      ctx.globalAlpha = 1;
    };

    const handleMouseMove = e => {
      const currentX = e.clientX;
      const currentY = e.clientY;

      if (lastPos) {
        const dx = currentX - lastPos.x;
        const dy = currentY - lastPos.y;
        const steps = Math.min(Math.ceil(Math.hypot(dx, dy) / 6), 40);
        for (let i = 1; i <= steps; i++) {
          drawDotGrid(lastPos.x + (dx * i) / steps, lastPos.y + (dy * i) / steps);
        }
      } else {
        drawDotGrid(currentX, currentY);
      }

      lastPos = { x: currentX, y: currentY };
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });

    let animId = 0;
    const loop = () => {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.globalAlpha = 0.055;
      ctx.fillRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      animId = requestAnimationFrame(loop);
    };
    animId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', resize);
    };
  }, [color]);

  return <StyledCanvas ref={canvasRef} aria-hidden="true" />;
};

MouseTrail.propTypes = {
  color: PropTypes.string,
};

export default MouseTrail;
